import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mic2 } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const { login, register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isRegister) {
        await register(name, email, password);
        toast.success(`Welcome, ${name}!`);
      } else {
        await login(email, password);
        toast.success("Signed in.");
      }
      navigate("/");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16 sm:px-6">
      <div className="glass rounded-2xl p-8">
        <div className="mb-8 flex flex-col items-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-md bg-amber-400 text-black glow-amber">
            <Mic2 className="h-7 w-7" strokeWidth={2.5} />
          </div>
          <h1 className="mt-4 font-display text-4xl text-white">
            {isRegister ? "JOIN THE CLUB" : "WELCOME BACK"}
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            {isRegister ? "Create an account to vote and submit clips." : "Sign in to keep the laughs going."}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <div className="space-y-2">
              <Label className="font-mono-ui text-[10px] uppercase tracking-widest text-slate-300">Name</Label>
              <Input
                required
                data-testid="auth-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="What should we call you?"
                className="bg-black/40 border-white/10 focus:border-amber-400"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label className="font-mono-ui text-[10px] uppercase tracking-widest text-slate-300">Email</Label>
            <Input
              required
              type="email"
              data-testid="auth-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-black/40 border-white/10 focus:border-amber-400"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-mono-ui text-[10px] uppercase tracking-widest text-slate-300">Password</Label>
            <Input
              required
              type="password"
              data-testid="auth-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6+ characters"
              className="bg-black/40 border-white/10 focus:border-amber-400"
            />
          </div>

          {error && <p className="text-sm text-[#FF2E55]" data-testid="auth-error">{error}</p>}

          <Button
            type="submit"
            disabled={busy}
            data-testid="auth-submit"
            className="w-full bg-amber-400 text-black hover:bg-amber-300 font-bold text-base tracking-widest uppercase btn-brutal"
          >
            {busy ? "..." : isRegister ? "Create account" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-400">
          {isRegister ? "Already have an account? " : "New here? "}
          <Link
            to={isRegister ? "/login" : "/register"}
            data-testid="auth-toggle-link"
            className="text-amber-400 underline underline-offset-2 hover:text-amber-300"
          >
            {isRegister ? "Sign in" : "Create one"}
          </Link>
        </p>
      </div>
    </div>
  );
}
